import { defineStore } from "pinia";
import axios from "@/utils/axios";
import readXlsxFile from "read-excel-file";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import { Modal } from "bootstrap";
import { useSharedStore } from "@/stores/shared-store";
import { useAuthStore } from "@/stores/auth-store";
import { useCourseFetchStore } from "@/stores/course/course-fetch-store.js";
import { useExamFetchStore } from "@/stores/exam/exam-fetch-store";
import { useRouter } from "vue-router";

export const useCourseDataStore = defineStore("CourseData", {
  state: () => ({
    sharedStore: useSharedStore(),
    authStore: useAuthStore(),
    courseFetchStore: useCourseFetchStore(),
    router: useRouter(),

    isUploading: false,
    uploadProgress: 0,
    uploadStatusText: "",

    addCourseForm: {
      course_group_id: null,
      title: null,
      description: "",
      short_description: null,
      level: null,
      prerequisite_course: null,
      duration: null,
      expired_date: null,
      status: "New",
      instructor: null,
      thumbnail: null,
    },
    addCourseContent: [],
    addCourseQuiz: [],

    approveCourseID: null,
    approveData: {
      status: "Active",
      level: null,
      prerequisite_course: null,
      duration: null,
      approve_by: null,
    },

    contentDraft: {
      title: null,
      type: null,
      filePath: null,
      isSkippable: false,
      sequence: 1,
      localIndex: null,
    },
    QuizDraft: {
      content_local_index: null,
      content: null,
      question: null,
      popup_time: null,
      option_a: null,
      option_b: null,
      option_c: null,
      option_d: null,
      correct_answer: null,
    },
    ExamDraft: {
      examType: "Written",
      courseID: null,
      multiple_choice_weight: 50,
      essay_weight: 50,
      time_limit_minutes: 60,
    },
    ExamQuestionDraft: {
      examID: null,
      questionText: null,
      questionType: null,
      option_a: null,
      option_b: null,
      option_c: null,
      option_d: null,
      correctAnswer: null,
    },
    examExcelFile: null,
    examQuestionsParsed: [],
    examConfigured: false,
    existingExamQuestionCount: 0,

    courseGroupInitial: {
      code: null,
      name: null,
      description: null,
    },
    courseGroupForm: {
      code: null,
      name: null,
      description: null,
    },
    selectedGroupId: null,

    quizAnswerDraft: {
      student: null,
      quiz: null,
      selected_answer: null,
      is_correct: true,
    },
  }),
  actions: {
    resetCourseForm() {
      this.isUploading = false;
      this.uploadProgress = 0;
      this.uploadStatusText = "";
      // Mutate in-place: komponen memegang referensi object yang sama dari setup()
      Object.assign(this.addCourseForm, {
        course_group_id: null,
        title: "",
        short_description: "",
        description: "",
        level: null,
        prerequisite_course: null,
        duration: null,
        expired_date: null,
        status: "New",
        instructor: null,
        thumbnail: null,
      });
      this.addCourseContent.splice(0, this.addCourseContent.length);
      this.addCourseQuiz.splice(0, this.addCourseQuiz.length);
      this.resetContentDraft();
      this.resetQuizDraft();
      this.clearExamDraft();
    },
    //Content
    NewContent() {
      this.resetContentDraft();

      const modalEl = document.getElementById("add-lesson");
      const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
      modal.show();
    },

    EditContent(index) {
      const existing = this.addCourseContent[index];
      this.contentDraft = {
        ...existing,
        index,
      };
      const modalEl = document.getElementById("add-lesson");
      const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
      modal.show();
    },

    saveDraft() {
      const draft = { ...this.contentDraft };

      if (draft.localIndex == null) {
        // Tambah konten baru
        const newIndex =
          this.addCourseContent.length > 0
            ? Math.max(...this.addCourseContent.map((c) => c.localIndex || 0)) +
              1
            : 1;

        draft.localIndex = newIndex;
        draft.sequence = this.addCourseContent.length + 1;

        this.addCourseContent.push(draft);
      } else {
        // Edit berdasarkan localIndex
        const idx = this.addCourseContent.findIndex(
          (item) => item.localIndex === draft.localIndex,
        );
        if (idx !== -1) {
          this.addCourseContent[idx] = { ...draft };
        }
      }
      this.resetContentDraft();
    },
    resetContentDraft() {
      this.contentDraft = {
        title: "",
        type: "",
        duration: null,
        isSkippable: false,
        sequence: null,
        filePath: null,
        localIndex: null,
      };
    },

    removeContentItem(index) {
      this.addCourseContent.splice(index, 1);
      // Perbarui sequence
      this.addCourseContent.forEach((item, i) => {
        item.sequence = i + 1;
      });
    },

    //Quiz
    NewQuizDialog() {
      this.QuizDraft = {
        content_local_index: null,
        content: null,
        question: null,
        popup_time: null,
        option_a: null,
        option_b: null,
        option_c: null,
        option_d: null,
        correct_answer: null,
      };

      const modalEl = document.getElementById("add-quiz");
      const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
      modal.show();
    },
    EditQuizDialog(index) {
      const existing = this.addCourseQuiz[index];
      this.QuizDraft = {
        ...existing,
        index,
      };
      const modalEl = document.getElementById("add-quiz");
      const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
      modal.show();
    },

    saveQuizDraft() {
      const draft = { ...this.QuizDraft };
      if (draft.index === null || draft.index === undefined) {
        // Tambah quiz baru
        this.addCourseQuiz.push({ ...draft });
      } else {
        // Update quiz yang sudah ada
        this.addCourseQuiz[draft.index] = { ...draft };
      }
    },
    resetQuizDraft() {
      this.QuizDraft = {
        content_local_index: null,
        content: null,
        question: null,
        popup_time: null,
        option_a: null,
        option_b: null,
        option_c: null,
        option_d: null,
        correct_answer: null,
      };
    },
    removeQuizItem(index) {
      this.addCourseQuiz.splice(index, 1);
    },

    clearExamDraft() {
      this.examQuestionsParsed.splice(0, this.examQuestionsParsed.length);
      this.examExcelFile = null;
      this.examConfigured = false;
      this.existingExamQuestionCount = 0;
      this.ExamDraft = {
        examID: null,
        examType: "Written",
        courseID: null,
        multiple_choice_weight: 50,
        essay_weight: 50,
        time_limit_minutes: 60,
      };
    },

    //Exam
    async parseExamExcel(file) {
      this.sharedStore.startRequest();
      try {
        const workbook = await readXlsxFile(file); // pakai xlsx atau read-excel-file
        const parsed = [];

        for (let row of workbook.slice(1)) {
          const [
            questionType,
            questionText,
            optionA,
            optionB,
            optionC,
            optionD,
            correctAnswer,
          ] = row;

          parsed.push({
            questionText,
            questionType,
            option_a: optionA,
            option_b: optionB,
            option_c: optionC,
            option_d: optionD,
            correctAnswer,
          });
        }

        this.examQuestionsParsed = parsed;
      } catch (error) {
        this.sharedStore.showError("Gagal membaca file Excel.");
      } finally {
        this.sharedStore.endRequest();
      }
    },

    async downloadExistingExamExcel() {
      const examFetchStore = useExamFetchStore();
      const examID =
        this.ExamDraft?.examID ||
        this.courseFetchStore?.basicCourseDetail?.exam?.examID;

      if (!examID) {
        Swal.fire({
          title: "Info",
          text: "Tidak ada exam sebelumnya yang ditemukan pada course ini.",
          icon: "info",
        });
        return;
      }

      this.sharedStore.startRequest();
      try {
        await examFetchStore.fetchQuestionsByExam(examID);
        const questions = examFetchStore.examQuestions;

        if (!questions || questions.length === 0) {
          Swal.fire({
            title: "Info",
            text: "Exam sebelumnya belum memiliki daftar soal.",
            icon: "info",
          });
          return;
        }

        // Urutan kolom sesuai format yang dibaca oleh parseExamExcel:
        // [questionType, questionText, option_a, option_b, option_c, option_d, correctAnswer]
        const rows = questions.map((q) => [
          q.questionType || "MultipleChoice",
          q.questionText || "",
          q.option_a || "",
          q.option_b || "",
          q.option_c || "",
          q.option_d || "",
          q.correctAnswer || "",
        ]);

        const header = [
          "Question Type",
          "Question Text",
          "Option A",
          "Option B",
          "Option C",
          "Option D",
          "Correct Answer",
        ];

        const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Questions");

        const courseTitle =
          this.addCourseForm.title ||
          this.courseFetchStore.basicCourseDetail?.title ||
          "Course";
        const safeTitle = courseTitle.replace(/[^a-zA-Z0-9_-]/g, "_");
        XLSX.writeFile(wb, `Soal_Exam_${safeTitle}.xlsx`);
      } catch (err) {
        console.error("Gagal export excel soal exam:", err);
        Swal.fire({
          title: "Error",
          text: "Gagal mengekspor soal exam sebelumnya ke Excel.",
          icon: "error",
        });
      } finally {
        this.sharedStore.endRequest();
      }
    },

    //Submit
    async submitCourseWithContents() {
      if (!this.validateCourseForm()) return;

      this.isUploading = true;
      this.uploadProgress = 0;
      this.uploadStatusText = "Mempersiapkan pengunggahan...";
      this.sharedStore.startRequest();
      try {
        const fullPayload = new FormData();

        // === Course data ===
        fullPayload.append(
          "course_group_id",
          this.addCourseForm.course_group_id,
        );
        fullPayload.append("title", this.addCourseForm.title);
        fullPayload.append("description", this.addCourseForm.description);
        fullPayload.append(
          "short_description",
          this.addCourseForm.short_description || "",
        );
        if (this.addCourseForm.expired_date) {
          fullPayload.append(
            "expired_date",
            new Date(this.addCourseForm.expired_date)
              .toISOString()
              .slice(0, 10),
          );
        }
        fullPayload.append("status", this.addCourseForm.status);
        fullPayload.append("instructor", this.addCourseForm.instructor);
        if (this.addCourseForm.level) {
          fullPayload.append("level", this.addCourseForm.level);
        }
        if (
          this.addCourseForm.level &&
          this.addCourseForm.level !== "Fundamental" &&
          this.addCourseForm.prerequisite_course
        ) {
          fullPayload.append(
            "prerequisite_course",
            this.addCourseForm.prerequisite_course,
          );
        }
        if (
          this.addCourseForm.duration !== null &&
          this.addCourseForm.duration !== undefined &&
          this.addCourseForm.duration !== ""
        ) {
          fullPayload.append("duration", this.addCourseForm.duration);
        }

        if (this.addCourseForm.thumbnail) {
          fullPayload.append("thumbnail", this.addCourseForm.thumbnail);
        }

        // === Contents ===
        this.addCourseContent.forEach((content, index) => {
          fullPayload.append(`contents[${index}][title]`, content.title);
          fullPayload.append(`contents[${index}][type]`, content.type);
          fullPayload.append(`contents[${index}][file]`, content.filePath); // <- penting: ganti dari filePath
          fullPayload.append(
            `contents[${index}][isSkippable]`,
            content.isSkippable ? "true" : "false",
          );
          fullPayload.append(`contents[${index}][sequence]`, content.sequence);
          fullPayload.append(
            `contents[${index}][localIndex]`,
            content.localIndex,
          );
        });

        // === Quizzes ===
        this.addCourseQuiz.forEach((quiz, index) => {
          fullPayload.append(
            `quizzes[${index}][content_local_index]`,
            quiz.content_local_index,
          );
          fullPayload.append(`quizzes[${index}][question]`, quiz.question);
          fullPayload.append(`quizzes[${index}][popup_time]`, quiz.popup_time);
          fullPayload.append(`quizzes[${index}][option_a]`, quiz.option_a);
          fullPayload.append(`quizzes[${index}][option_b]`, quiz.option_b);
          fullPayload.append(`quizzes[${index}][option_c]`, quiz.option_c);
          fullPayload.append(`quizzes[${index}][option_d]`, quiz.option_d);
          fullPayload.append(
            `quizzes[${index}][correct_answer]`,
            quiz.correct_answer,
          );
        });
        // === Exam ===
        fullPayload.append("exams[0][examType]", this.ExamDraft.examType);
        fullPayload.append(
          "exams[0][multiple_choice_weight]",
          this.ExamDraft.multiple_choice_weight,
        );
        fullPayload.append(
          "exams[0][essay_weight]",
          this.ExamDraft.essay_weight,
        );
        fullPayload.append(
          "exams[0][time_limit_minutes]",
          this.ExamDraft.time_limit_minutes,
        );

        // === Exam Questions ===
        this.examQuestionsParsed.forEach((question, index) => {
          fullPayload.append(
            `exams[0][questions][${index}][questionText]`,
            question.questionText,
          );
          fullPayload.append(
            `exams[0][questions][${index}][questionType]`,
            question.questionType,
          );
          fullPayload.append(
            `exams[0][questions][${index}][option_a]`,
            question.option_a || "",
          );
          fullPayload.append(
            `exams[0][questions][${index}][option_b]`,
            question.option_b || "",
          );
          fullPayload.append(
            `exams[0][questions][${index}][option_c]`,
            question.option_c || "",
          );
          fullPayload.append(
            `exams[0][questions][${index}][option_d]`,
            question.option_d || "",
          );
          fullPayload.append(
            `exams[0][questions][${index}][correctAnswer]`,
            question.correctAnswer,
          );
        });

        const response = await axios.post(
          `${this.authStore.Url}/api/courses/full-course/`,
          fullPayload,
          {
            headers: {
              Authorization: `Bearer ${this.authStore.token}`,
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total) {
                const percent = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total,
                );
                this.uploadProgress = percent;
                const loadedMB = (progressEvent.loaded / (1024 * 1024)).toFixed(1);
                const totalMB = (progressEvent.total / (1024 * 1024)).toFixed(1);
                if (percent < 100) {
                  this.uploadStatusText = `Mengunggah media: ${loadedMB} MB / ${totalMB} MB (${percent}%)`;
                } else {
                  this.uploadStatusText =
                    "File berhasil terkirim. Sedang memproses dan menyimpan data di server...";
                }
              }
            },
          },
        );

        const courseID = response.data.course_id;
        this.sharedStore.showSuccess(
          "Course dan semua data berhasil disimpan.",
        );

        if (this.authStore.role == "teacher") {
          this.router.push("/instructor/instructor-course");
        } else {
          this.router.push("/principal/Course");
        }
        this.resetCourseForm(); // <-- reset form + content/quiz/exam

        return courseID;
      } catch (error) {
        this.sharedStore.showError(
          "Gagal menyimpan course: ",
          error.response?.data || error,
        );
        throw error;
      } finally {
        this.isUploading = false;
        this.uploadProgress = 0;
        this.uploadStatusText = "";
        this.sharedStore.endRequest();
      }
    },

    async updateCourseWithContents(courseID) {
      const sharedStore = useSharedStore();
      const authStore = useAuthStore();

      if (!this.addCourseForm.title || !this.addCourseForm.course_group_id) {
        sharedStore.showError("Data course tidak lengkap.");
        return;
      }

      this.isUploading = true;
      this.uploadProgress = 0;
      this.uploadStatusText = "Mempersiapkan pengunggahan...";
      sharedStore.startRequest();
      try {
        const formData = new FormData();

        // === Course data ===
        formData.append("course_group_id", this.addCourseForm.course_group_id);
        formData.append("title", this.addCourseForm.title);
        formData.append("description", this.addCourseForm.description);
        formData.append(
          "short_description",
          this.addCourseForm.short_description || "",
        );
        if (this.addCourseForm.expired_date) {
          formData.append(
            "expired_date",
            new Date(this.addCourseForm.expired_date)
              .toISOString()
              .slice(0, 10),
          );
        }
        let updatedStatus = this.addCourseForm.status;
        if (
          this.addCourseForm.status === "Active" ||
          this.addCourseForm.status === "Need Approval Edit"
        ) {
          updatedStatus = "Need Approval Edit";
        }
        formData.append("status", updatedStatus);
        formData.append("instructor", this.addCourseForm.instructor);
        if (this.addCourseForm.level) {
          formData.append("level", this.addCourseForm.level);
        }
        if (
          this.addCourseForm.level &&
          this.addCourseForm.level !== "Fundamental" &&
          this.addCourseForm.prerequisite_course
        ) {
          formData.append(
            "prerequisite_course",
            this.addCourseForm.prerequisite_course,
          );
        } else {
          formData.append("prerequisite_course", "");
        }
        if (
          this.addCourseForm.duration !== null &&
          this.addCourseForm.duration !== undefined &&
          this.addCourseForm.duration !== ""
        ) {
          formData.append("duration", this.addCourseForm.duration);
        }

        if (this.addCourseForm.thumbnail) {
          formData.append("thumbnail", this.addCourseForm.thumbnail);
        }

        // === Contents ===
        this.addCourseContent.forEach((content, index) => {
          formData.append(`contents[${index}][title]`, content.title);
          formData.append(`contents[${index}][type]`, content.type);
          formData.append(`contents[${index}][file]`, content.filePath);
          formData.append(
            `contents[${index}][isSkippable]`,
            content.isSkippable ? "true" : "false",
          );
          formData.append(`contents[${index}][sequence]`, content.sequence);
          formData.append(`contents[${index}][localIndex]`, content.localIndex);
        });

        // === Quizzes ===
        this.addCourseQuiz.forEach((quiz, index) => {
          formData.append(
            `quizzes[${index}][content_local_index]`,
            quiz.content_local_index,
          );
          formData.append(`quizzes[${index}][question]`, quiz.question);
          formData.append(`quizzes[${index}][popup_time]`, quiz.popup_time);
          formData.append(`quizzes[${index}][option_a]`, quiz.option_a);
          formData.append(`quizzes[${index}][option_b]`, quiz.option_b);
          formData.append(`quizzes[${index}][option_c]`, quiz.option_c);
          formData.append(`quizzes[${index}][option_d]`, quiz.option_d);
          formData.append(
            `quizzes[${index}][correct_answer]`,
            quiz.correct_answer,
          );
        });

        // === Exam ===
        // Hanya kirim exams jika ada soal baru yang diunggah agar tidak menghapus soal lama yang berelasi dengan jawaban siswa
        if (this.examQuestionsParsed && this.examQuestionsParsed.length > 0) {
          formData.append("exams[0][examType]", this.ExamDraft.examType);
          formData.append(
            "exams[0][multiple_choice_weight]",
            this.ExamDraft.multiple_choice_weight,
          );
          formData.append("exams[0][essay_weight]", this.ExamDraft.essay_weight);
          formData.append(
            "exams[0][time_limit_minutes]",
            this.ExamDraft.time_limit_minutes,
          );

          // === Exam Questions ===
          this.examQuestionsParsed.forEach((question, index) => {
            formData.append(
              `exams[0][questions][${index}][questionText]`,
              question.questionText,
            );
            formData.append(
              `exams[0][questions][${index}][questionType]`,
              question.questionType,
            );
            formData.append(
              `exams[0][questions][${index}][option_a]`,
              question.option_a || "",
            );
            formData.append(
              `exams[0][questions][${index}][option_b]`,
              question.option_b || "",
            );
            formData.append(
              `exams[0][questions][${index}][option_c]`,
              question.option_c || "",
            );
            formData.append(
              `exams[0][questions][${index}][option_d]`,
              question.option_d || "",
            );
            formData.append(
              `exams[0][questions][${index}][correctAnswer]`,
              question.correctAnswer,
            );
          });
        }

        const response = await axios.put(
          `${authStore.Url}/api/courses/full-course/${courseID}/`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total) {
                const percent = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total,
                );
                this.uploadProgress = percent;
                const loadedMB = (progressEvent.loaded / (1024 * 1024)).toFixed(1);
                const totalMB = (progressEvent.total / (1024 * 1024)).toFixed(1);
                if (percent < 100) {
                  this.uploadStatusText = `Mengunggah media: ${loadedMB} MB / ${totalMB} MB (${percent}%)`;
                } else {
                  this.uploadStatusText =
                    "File berhasil terkirim. Sedang memproses dan menyimpan data di server...";
                }
              }
            },
          },
        );

        sharedStore.showSuccess("Course berhasil diperbarui.");

        if (this.authStore.role == "teacher") {
          this.router.push("/instructor/instructor-course");
        } else {
          this.router.push("/principal/Course");
        }

        this.resetCourseForm(); // <-- reset form + content/quiz/exam

        return response.data;
      } catch (error) {
        sharedStore.showError(
          "Gagal memperbarui course: ",
          error.response?.data || error,
        );
        throw error;
      } finally {
        this.isUploading = false;
        this.uploadProgress = 0;
        this.uploadStatusText = "";
        sharedStore.endRequest();
      }
    },

    async loadCourseForEdit(id) {
      this.sharedStore.startRequest();
      try {
        const data = await this.courseFetchStore.fetchCourseDetailBasic(id);
        /* console.log(data); */
        // === Course Form ===
        Object.assign(this.addCourseForm, {
          course_group_id: data.course_group?.id || null,
          title: data.title,
          short_description: data.short_description || "",
          description: data.description || "",
          duration: data.duration || 0,
          expired_date: data.expired_date || null,
          status: data.status || "New",
          instructor: data.instructor || "",
          level: data.level || "",
          prerequisite_course:
            data.prerequisite_course?.course_id ||
            data.prerequisite_course ||
            null,
          thumbnail: data.thumbnail || null,
        });

        // === Contents ===
        this.addCourseContent.splice(
          0,
          this.addCourseContent.length,
          ...(data.content || []).map((c, index) => ({
            title: c.title,
            type: c.type,
            filePath: c.filePath || null,
            duration: c.duration,
            isSkippable: c.isSkippable,
            sequence: c.sequence,
            contentID: c.contentID, // disimpan agar bisa dicocokkan di quiz
            localIndex: index + 1,
          })),
        );

        // === Quizzes ===
        this.addCourseQuiz.splice(
          0,
          this.addCourseQuiz.length,
          ...(data.quizzes || []).map((q) => ({
            content_local_index: this.findContentLocalIndex(q.content),
            question: q.question,
            popup_time: q.popup_time,
            option_a: q.option_a,
            option_b: q.option_b,
            option_c: q.option_c,
            option_d: q.option_d,
            correct_answer: q.correct_answer,
          })),
        );

        // === Exam ===
        if (data.exam) {
          const qCount = data.exam.question_count || 0;
          Object.assign(this.ExamDraft, {
            examID: data.exam.examID,
            courseID: data.exam.courseID,
            examType: data.exam.examType || "Written",
            multiple_choice_weight: data.exam.multiple_choice_weight,
            essay_weight: data.exam.essay_weight,
            time_limit_minutes: data.exam.time_limit_minutes,
            question_count: qCount,
          });
          this.existingExamQuestionCount = qCount;
          this.examConfigured = true;

          // Jika qCount masih 0 tapi ada examID, fetch langsung dari API questions untuk sinkronisasi
          if (qCount === 0 && data.exam.examID) {
            const examFetchStore = useExamFetchStore();
            examFetchStore
              .fetchQuestionsByExam(data.exam.examID)
              .then((questions) => {
                if (questions && questions.length > 0) {
                  this.existingExamQuestionCount = questions.length;
                  if (this.ExamDraft) {
                    this.ExamDraft.question_count = questions.length;
                  }
                }
              })
              .catch((err) => console.warn("Gagal fetch questions count:", err));
          }
        } else {
          this.existingExamQuestionCount = 0;
          this.clearExamDraft();
        }

        this.examQuestionsParsed.splice(0, this.examQuestionsParsed.length);
      } catch (error) {
        console.error("Gagal memuat data untuk edit:", error);
        this.sharedStore.showError("Gagal memuat data course.");
      } finally {
        this.sharedStore.endRequest();
      }
    },

    findContentLocalIndex(contentID) {
      const found = this.addCourseContent.find(
        (c) => c.contentID === contentID,
      );
      return found ? found.localIndex : null;
    },

    async deleteCourse(courseOrId) {
      const courseId =
        typeof courseOrId === "object" && courseOrId != null
          ? (courseOrId.course_id ?? courseOrId.id)
          : courseOrId;
      const id = Number(courseId);

      if (!Number.isFinite(id) || id <= 0) {
        this.sharedStore.showError(
          `ID Course tidak valid${courseId != null ? `: ${courseId}` : ""}`,
        );
        return false;
      }
      if (this.sharedStore.loading) return false;

      this.sharedStore.startRequest();
      try {
        const response = await axios.delete(
          `${this.authStore.Url}/api/courses/manage/${id}/`,
        );
        // Optimistic local remove so UI updates even if list refresh races.
        this.courseFetchStore.courseList = (
          this.courseFetchStore.courseList || []
        ).filter((c) => Number(c.course_id ?? c.id) !== id);
        this.courseFetchStore.courseCreatedList = (
          this.courseFetchStore.courseCreatedList || []
        ).filter((c) => Number(c.course_id ?? c.id) !== id);

        await this.courseFetchStore.fetchCourseList();
        this.sharedStore.showSuccess("Course berhasil di Hapus.");
        return response.data ?? true;
      } catch (error) {
        console.error("Gagal Delete course:", error);
        // Double-submit / already soft-deleted → treat as success if gone from list.
        if (error?.response?.status === 404) {
          await this.courseFetchStore.fetchCourseList();
          const stillExists = (this.courseFetchStore.courseList || []).some(
            (c) => Number(c.course_id ?? c.id) === id,
          );
          if (!stillExists) {
            this.sharedStore.showSuccess("Course berhasil di Hapus.");
            return true;
          }
        }
        this.sharedStore.showError(
          error?.response?.data?.error ||
            error?.response?.data?.detail ||
            error?.response?.data?.message ||
            "Gagal menghapus course",
        );
        return false;
      } finally {
        this.sharedStore.endRequest();
      }
    },

    validateCourseForm() {
      const errors = [];

      // --- Course Form Validation ---
      if (!this.addCourseForm.course_group_id)
        errors.push("Grup course harus diisi.");
      if (!this.addCourseForm.title) errors.push("Judul course harus diisi.");
      if (!this.addCourseForm.description)
        errors.push("Deskripsi harus diisi.");
      if (!this.addCourseForm.instructor)
        errors.push("Instruktur harus diisi.");
      if (!this.addCourseForm.level) {
        errors.push("Level course harus dipilih.");
      } else if (this.addCourseForm.level !== "Fundamental") {
        if (!this.addCourseForm.prerequisite_course) {
          errors.push(
            "Course prasyarat (level Fundamental) harus dipilih jika level bukan Fundamental.",
          );
        }
      }
      if (
        this.addCourseForm.duration === null ||
        this.addCourseForm.duration === undefined ||
        this.addCourseForm.duration === ""
      ) {
        errors.push("Durasi course harus diisi.");
      } else if (Number(this.addCourseForm.duration) <= 0) {
        errors.push("Durasi course harus lebih dari 0 hari.");
      }

      // --- Content Validation ---
      if (this.addCourseContent.length === 0)
        errors.push("Minimal 1 konten pembelajaran harus ditambahkan.");

      for (const [i, content] of this.addCourseContent.entries()) {
        if (!content.title) errors.push(`Konten ${i + 1}: Judul wajib diisi.`);
        if (!content.type)
          errors.push(`Konten ${i + 1}: Tipe konten wajib diisi.`);
        if (!content.filePath)
          errors.push(`Konten ${i + 1}: File wajib diunggah.`);
      }

      // --- Exam Validation ---
      const examType = this.ExamDraft.examType;
      if (examType !== "Practical") {
        if (
          !this.ExamDraft.multiple_choice_weight ||
          !this.ExamDraft.essay_weight ||
          !this.ExamDraft.time_limit_minutes
        ) {
          errors.push("Pengaturan ujian tidak lengkap.");
        }

        // --- Exam Question Validation ---
        if (this.examQuestionsParsed.length === 0)
          errors.push("Minimal 1 soal ujian harus diunggah dari Excel.");

        for (const [i, q] of this.examQuestionsParsed.entries()) {
          if (!q.questionText)
            errors.push(`Soal ${i + 1}: Pertanyaan wajib diisi.`);
          if (!q.questionType) {
            errors.push(`Soal ${i + 1}: Tipe soal wajib diisi.`);
          } else if (
            q.questionType.toLowerCase() !== "essay" &&
            !q.correctAnswer
          ) {
            errors.push(
              `Soal ${i + 1}: Jawaban benar wajib diisi untuk soal non-essay.`,
            );
          }
        }
      }

      // --- Quiz Validation (Opsional) ---
      for (const [i, quiz] of this.addCourseQuiz.entries()) {
        if (!quiz.question)
          errors.push(`Quiz ${i + 1}: Pertanyaan wajib diisi.`);
        if (!quiz.correct_answer)
          errors.push(`Quiz ${i + 1}: Jawaban benar wajib diisi.`);
      }

      if (errors.length > 0) {
        this.sharedStore.showError(errors);
        return false;
      }

      return true;
    },

    async approveCourse(courseId = null) {
      this.sharedStore.startRequest();
      const id = courseId || this.approveCourseID;
      try {
        const payload = {
          status: "Active",
          approve_by: this.authStore.currentUser?.id,
        };
        const response = await axios.patch(
          `${this.authStore.Url}/api/courses/manage/${id}/`,
          payload,
        );
        // Update local state jika perlu
        if (this.courseFetchStore.basicCourseDetail) {
          this.courseFetchStore.basicCourseDetail.status = "Active";
        }
        await this.courseFetchStore.fetchCourseList();
        this.sharedStore.showSuccess("Course berhasil di Approve.");
        return response.data;
      } catch (error) {
        console.error("Gagal approve course:", error);
        this.sharedStore.showError(
          "Gagal approve course: " +
            (error.response?.data?.message ||
              error.response?.data?.error ||
              error.message ||
              ""),
        );
        throw error;
      } finally {
        this.sharedStore.endRequest();
      }
    },

    //Group
    async createGroup() {
      this.sharedStore.startRequest();
      try {
        const response = await axios.post(
          `${this.authStore.Url}/api/courses/course-groups/`,
          this.courseGroupForm,
          {
            headers: {
              Authorization: `Bearer ${this.authStore.token}`,
              "Content-Type": "application/json",
            },
          },
        );

        this.sharedStore.showSuccess("Course Group berhasil dibuat");
        this.courseFetchStore.fetchGroupList();
        return response.data;
      } catch (error) {
        this.sharedStore.showError(error.response.data.error);
      } finally {
        this.sharedStore.endRequest(); // Selalu dijalankan apapun hasilnya
      }
    },
    async updateGroup() {
      this.sharedStore.startRequest();
      try {
        if (!this.selectedGroupId) {
          throw new Error("ID Group belum dipilih");
        }

        const response = await axios.put(
          `${this.authStore.Url}/api/courses/course-groups/${this.selectedGroupId}/`,
          this.courseGroupForm,
          {
            headers: {
              Authorization: `Bearer ${this.authStore.token}`,
              "Content-Type": "application/json",
            },
          },
        );

        this.sharedStore.showSuccess("Course Group berhasil diperbarui");
        this.courseFetchStore.fetchGroupList();
        this.resetCourseGroupForm();
        this.selectedGroupId = null;

        return response.data;
      } catch (error) {
        this.sharedStore.showError(
          error.response?.data?.error || "Gagal memperbarui group",
        );
      } finally {
        this.sharedStore.endRequest();
      }
    },
    async deleteGroup(groupOrId) {
      const groupId =
        typeof groupOrId === "object" && groupOrId != null
          ? (groupOrId.course_group_id ?? groupOrId.id)
          : groupOrId;
      const id = Number(groupId);

      if (!Number.isFinite(id) || id <= 0) {
        this.sharedStore.showError(
          `ID Group tidak valid${groupId != null ? `: ${groupId}` : ""}`,
        );
        return;
      }
      if (this.sharedStore.loading) return;

      this.sharedStore.startRequest();
      try {
        const response = await axios.delete(
          `${this.authStore.Url}/api/courses/course-groups/${id}/`,
          {
            headers: {
              Authorization: `Bearer ${this.authStore.token}`,
              "Content-Type": "application/json",
            },
          },
        );

        await this.courseFetchStore.fetchGroupList();
        this.sharedStore.showSuccess("Course Group berhasil dihapus");

        return response.data;
      } catch (error) {
        // Double-submit / retry setelah sukses: anggap sukses jika sudah hilang dari list.
        if (error?.response?.status === 404) {
          await this.courseFetchStore.fetchGroupList();
          const stillExists = (this.courseFetchStore.groupList || []).some(
            (group) =>
              Number(group.course_group_id ?? group.id) === id,
          );
          if (!stillExists) {
            this.sharedStore.showSuccess("Course Group berhasil dihapus");
            return;
          }
        }
        this.sharedStore.showError(
          error.response?.data?.error ||
            error.response?.data?.detail ||
            "Gagal menghapus group",
        );
      } finally {
        this.sharedStore.endRequest();
      }
    },

    resetCourseGroupForm() {
      this.courseGroupForm = { ...this.courseGroupInitial };
    },

    async submitQuisAnswer() {
      this.sharedStore.startRequest();
      try {
        const response = await axios.post(
          `${this.authStore.Url}/api/courses/quiz-submission/`,
          this.quizAnswerDraft,
        );

        this.sharedStore.showSuccess("Quiz berhasil dikirim");
        return response.data;
      } catch (error) {
        this.sharedStore.showError(error.response.data.error);
      } finally {
        this.sharedStore.endRequest(); // Selalu dijalankan apapun hasilnya
      }
    },
  },
});
